import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material";

export type ThemeMode = "light" | "dark";

export interface ThemePalette {
    mode: ThemeMode;
    background: string;
    paper: string;
    surface: string;
    surfaceAlt: string;
    text: string;
    textSecondary: string;
    border: string;
    divider: string;
    shadow: string;
    cardGradient: string;
    iconBg: string;
    inputBg: string;
}

const lightPalette: ThemePalette = {
    mode: "light",
    background: "#e5e5e5",
    paper: "#ffffff",
    surface: "#ffffff",
    surfaceAlt: "#F8F8F8",
    text: "#000000",
    textSecondary: "#404040",
    border: "#e1e1e1",
    divider: "#AAAAAA",
    shadow: "rgba(0, 0, 0, 0.25)",
    cardGradient: "linear-gradient(156deg, rgb(247, 146, 30) 0%, rgb(179, 95, 0) 10%, rgb(26, 26, 26) 40%, rgb(0, 0, 0) 100%)",
    // cardGradient: "linear-gradient(135deg, #f7921e 0%, #b35f00 17%, #1a1a1a 80%, #000000 100%)",
    iconBg: "#ffffff",
    inputBg: "#ffffff",
};

const darkPalette: ThemePalette = {
    mode: "dark",
    background: "#121212",
    paper: "#1e1e1e",
    surface: "#1e1e1e",
    surfaceAlt: "#2a2a2a",
    text: "#f5f5f5",
    textSecondary: "#cfcfcf",
    border: "#3a3a3a",
    divider: "#555555",
    shadow: "rgba(0, 0, 0, 0.7)",
    cardGradient: "linear-gradient(135deg, #000000 0%, #1a1a1a 40%, #3d2108 75%, #f7921e 100%)",
    iconBg: "#2a2a2a",
    inputBg: "#2a2a2a",
};

interface ThemeContextValue {
    mode: ThemeMode;
    palette: ThemePalette;
    toggleTheme: () => void;
    setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeContextValue>({
    mode: "dark",
    palette: darkPalette,
    toggleTheme: () => {},
    setMode: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

type Props = { children: React.ReactNode };

const STORAGE_KEY = "appThemeMode_v2";

const ThemeContextProvider: React.FC<Props> = ({ children }) => {
    const [mode, setModeState] = useState<ThemeMode>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved === "light" ? "light" : "dark";
    });

    const palette = mode === "dark" ? darkPalette : lightPalette;

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, mode);
        document.body.style.backgroundColor = palette.background;
        document.body.setAttribute("data-theme", mode);
    }, [mode, palette.background]);

    const muiTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    background: {
                        default: palette.background,
                        paper: palette.paper,
                    },
                    text: {
                        primary: palette.text,
                        secondary: palette.textSecondary,
                    },
                    primary: {
                        main: "#f7921e",
                    },
                },
            }),
        [mode, palette]
    );

    const setMode = (m: ThemeMode) => setModeState(m);
    const toggleTheme = () => setModeState((m) => (m === "dark" ? "light" : "dark"));

    return (
        <ThemeModeContext.Provider value={{ mode, palette, toggleTheme, setMode }}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeModeContext.Provider>
    );
};

export default ThemeContextProvider;
